import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Music, Instagram, ExternalLink, Disc3 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = "";
const API = `${BACKEND_URL}/api`;

const ArtistsPage = () => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArtist, setSelectedArtist] = useState(null);

  useEffect(() => {
    const fetchArtists = async () => {
      try {
        const response = await axios.get(`${API}/artists`);
        setArtists(response.data.artists || response.data);
      } catch (error) {
        console.error('Error fetching artists:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArtists();
  }, []);

  return (
    <div className="min-h-screen py-12 px-6 md:px-12" data-testid="artists-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <span className="font-heading text-sm tracking-widest text-[#FFB347] mb-2 block">
            NOS ARTISTES
          </span>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">
            La Famille Sapito
          </h1>
          <p className="text-[#A8A29E] text-lg max-w-2xl">
            Découvrez les artistes talentueux qui font la richesse de notre catalogue. Chacun apporte sa vision unique de la musique électronique.
          </p>
        </motion.div>

        {/* Artists Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="card animate-pulse">
                <div className="aspect-[4/3] bg-[#1a1a1a]" />
                <div className="p-6 space-y-3">
                  <div className="h-6 bg-[#1a1a1a] rounded w-1/2" />
                  <div className="h-4 bg-[#1a1a1a] rounded w-full" />
                  <div className="h-4 bg-[#1a1a1a] rounded w-3/4" />
                </div>
              </div>
            ))}
          </div>
        ) : artists.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <Disc3 className="w-16 h-16 text-[#A8A29E] mx-auto mb-4" />
            <h3 className="font-display text-xl mb-2">Artistes à venir</h3>
            <p className="text-[#A8A29E]">Notre roster d'artistes sera bientôt disponible</p>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {artists.map((artist, index) => (
              <motion.div
                key={artist.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card group cursor-pointer"
                onClick={() => setSelectedArtist(artist)}
                data-testid={`artist-card-${index}`}
              >
                {/* Image */}
                <div className="aspect-[4/3] relative overflow-hidden">
                  <img
                    src={artist.image || "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400"}
                    alt={artist.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908] via-[#0A0908]/50 to-transparent" />
                  
                  {/* Overlay Info */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="font-display text-2xl font-bold mb-2 group-hover:text-[#FFB347] transition-colors">
                      {artist.name}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {artist.genres?.slice(0, 3).map((genre, i) => (
                        <span 
                          key={i}
                          className="px-2 py-1 text-xs rounded-full bg-[#8B4513]/30 text-[#FFB347]"
                        >
                          {genre}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Bio Preview */}
                <div className="p-6">
                  <p className="text-[#A8A29E] text-sm line-clamp-3 mb-4">
                    {artist.bio}
                  </p>
                  
                  {/* Social Links */}
                  <div className="flex gap-3">
                    {artist.soundcloud_url && (
                      <a
                        href={artist.soundcloud_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="w-9 h-9 rounded-full border border-[rgba(139,69,19,0.3)] flex items-center justify-center text-[#A8A29E] hover:text-[#FFB347] hover:border-[#FFB347] transition-all"
                        data-testid={`artist-soundcloud-${index}`}
                      >
                        <Music className="w-4 h-4" />
                      </a>
                    )}
                    {artist.instagram && (
                      <a
                        href={`https://instagram.com/${artist.instagram.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="w-9 h-9 rounded-full border border-[rgba(139,69,19,0.3)] flex items-center justify-center text-[#A8A29E] hover:text-[#FFB347] hover:border-[#FFB347] transition-all"
                        data-testid={`artist-instagram-${index}`}
                      >
                        <Instagram className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Artist Modal */}
        {selectedArtist && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0A0908]/90 backdrop-blur-sm"
            onClick={() => setSelectedArtist(null)}
            data-testid="artist-modal"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#141210] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-[rgba(139,69,19,0.3)]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="relative aspect-video">
                <img
                  src={selectedArtist.image || "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600"}
                  alt={selectedArtist.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#141210] via-transparent to-transparent" />
                <button
                  onClick={() => setSelectedArtist(null)}
                  className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#0A0908]/50 backdrop-blur-sm flex items-center justify-center text-[#F5F5F0] hover:bg-[#FFB347] hover:text-[#0A0908] transition-all"
                  data-testid="close-modal-btn"
                >
                  ×
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-8 -mt-16 relative">
                <h2 className="font-display text-3xl font-bold mb-2">
                  {selectedArtist.name}
                </h2>
                
                {/* Genres */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {selectedArtist.genres?.map((genre, i) => (
                    <span 
                      key={i}
                      className="px-3 py-1 text-sm rounded-full bg-[#8B4513]/30 text-[#FFB347]"
                    >
                      {genre}
                    </span>
                  ))}
                </div>

                {/* Bio */}
                <p className="text-[#A8A29E] leading-relaxed mb-8">
                  {selectedArtist.bio}
                </p>

                {/* Links */}
                <div className="flex gap-4">
                  {selectedArtist.soundcloud_url && (
                    <a
                      href={selectedArtist.soundcloud_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary inline-flex items-center gap-2"
                      data-testid="modal-soundcloud-link"
                    >
                      <Music className="w-5 h-5" />
                      SoundCloud
                    </a>
                  )}
                  {selectedArtist.instagram && (
                    <a
                      href={`https://instagram.com/${selectedArtist.instagram.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary inline-flex items-center gap-2"
                      data-testid="modal-instagram-link"
                    >
                      <Instagram className="w-5 h-5" />
                      Instagram
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Join Us CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 p-8 bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] text-center"
        >
          <h3 className="font-display text-2xl font-bold mb-4">
            Rejoignez l'Aventure
          </h3>
          <p className="text-[#A8A29E] mb-6 max-w-xl mx-auto">
            Vous êtes producteur et souhaitez rejoindre le label ? Envoyez-nous votre démo et faites partie de la famille Sapito-Records.
          </p>
          <a
            href="/contact"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="join-us-btn"
          >
            <ExternalLink className="w-5 h-5" />
            Envoyer une Démo
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default ArtistsPage;
